import { Component, input, output, signal, effect, inject, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs';
import { ModalComponent } from '@abp/ng.theme.shared';
import { LocalizationService } from '@abp/ng.core';
import { SearchResultTable } from '../search-result-table/search-result-table';
import { CrudEntityConfig } from '../crud-page/models/crud-entity-config';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { ZnButton } from '../zn-button/zn-button';
import { localizeWithFallback } from '../utils/localization-utils';

interface TechnicalNameField {
  id: number;
  naturalName: string;
  technicalName: string;
}

@Component({
  selector: 'app-technical-names-report-modal',
  standalone: true,
  imports: [ModalComponent, SearchResultTable, ReactiveFormsModule, ZnButton],
  templateUrl: './technical-names-report-modal.html',
  styleUrls: ['./technical-names-report-modal.scss'],
})
export class TechnicalNamesReportModalComponent {
  config = input<CrudEntityConfig | null>(null);
  isVisible = input<boolean>(false);
  isVisibleChange = output<boolean>();

  private readonly localizationService = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);
  readonly localizationResource = ZendraLocalizationServices;

  private readonly localizationKeys = {
    title: 'TableTechnicalNamesReport',
    table: 'Table',
    technicalName: 'TechnicalName',
    searchField: 'SearchField',
    noResults: 'NoResultsFound',
    close: 'Close',
    naturalName: 'NaturalName'
  } as const;

  private readonly internalFields = signal<TechnicalNameField[]>([]);
  readonly filteredFields = signal<TechnicalNameField[]>([]);

  readonly tableDisplayName = computed(() => {
    const cfg = this.config();
    return cfg ? this.getDisplayName(cfg.localizationKey) : '';
  });

  readonly tableTechnicalName = computed(() => {
    const cfg = this.config();
    return cfg?.entityName ?? '';
  });

  readonly localizedStrings = computed(() => ({
    title: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.title}`),
    table: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.table}`),
    technicalName: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.technicalName}`),
    searchField: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.searchField}`),
    noResults: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.noResults}`),
    close: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.close}`),
    naturalName: localizeWithFallback(this.localizationService, `${this.localizationResource.LanguageService}::${this.localizationKeys.naturalName}`)
  }));

  readonly reportColumns = computed(() => [
    {
      id: 'naturalName',
      name: this.localizedStrings().naturalName,
      field: 'naturalName',
      minWidth: 200,
    },
    {
      id: 'technicalName',
      name: this.localizedStrings().technicalName,
      field: 'technicalName',
      minWidth: 200,
    },
  ]);

  readonly tableOptions = {
    enableHeaderMenu: false,
    enableHeaderButton: false,
    enableGridMenu: false,
    enableColumnPicker: false,
    enableAutoResize: true,
    forceFitColumns: true,
    enableContextMenu:true,
    rowHeight: 40,
    gridWidth: 'calc(100% - 3px)',
  };
  readonly searchControl = new FormControl('');

  constructor() {
    effect(() => {
      const cfg = this.config();
      if (!cfg?.fields?.length) {
        this.internalFields.set([]);
        this.filteredFields.set([]);
        return;
      }

      const mapped = cfg.fields.map((f, i) => ({
        id: i + 1,
        naturalName: localizeWithFallback(this.localizationService, 
          (typeof f.column?.name === 'string' ? f.column.name : f.label)
        ),
        technicalName: this.getTechnicalName(cfg.entityName, f.key),
      }));

      this.internalFields.set(mapped);
      this.filteredFields.set(mapped);
    });

    effect(() => {
      if (this.isVisible()) {
        this.searchControl.setValue('', { emitEvent: false });
        this.filteredFields.set([...this.internalFields()]);
      }
    });
    
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => this.applyFilter(value ?? ''));
  }

  onVisibleChange(value: boolean): void {
    this.isVisibleChange.emit(value);
  }

  private getDisplayName(localizationKey: string): string {
    const translated = localizeWithFallback(this.localizationService, localizationKey);
    return translated !== localizationKey ? translated : localizationKey;
  }

  private applyFilter(query: string): void {
    const q = this.normalizeString(query);
    const source = this.internalFields();

    this.filteredFields.set(
      q
        ? source.filter(f =>
            this.normalizeString(f.naturalName).includes(q) ||
            this.normalizeString(f.technicalName).includes(q),
          )
        : [...source],
    );
  }

  private normalizeString(s: string): string {
    return s
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();
  }

  private capitalizeFirst(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  private getTechnicalName(entity: string, key: string): string {
    const lower = key.toLowerCase();
    if (lower === 'id') return `${this.capitalizeFirst(entity)}Id`;
    return this.capitalizeFirst(key);
  }
}
